import { type ReactNode, useCallback, useMemo, useState } from 'react';

import { type GallerySectionAddition } from '@air/component-gallery-view';
import { useBreakpointsContext } from '@air/provider-media-query';

import { type GalleryMetadata } from '~/components/Gallery/galleryMetadata';
import { ListSectionHeader } from '~/components/Shared/ListSectionHeader';
import {
  WKSPC_GALLERY_FIRST_SECTION_GAP_DESKTOP,
  WKSPC_GALLERY_FIRST_SECTION_GAP_MOBILE,
} from '~/constants/WorkspaceSpacing';

export const SECTIONS_DISTANCE = 54;

type GallerySectionHeader = {
  groupName: string;
  isFirstSection: boolean;
  containerWidth: number;
  title: ReactNode;
  containerPadding: number;
  onSectionCollapse?: (isCollapsed: boolean) => void;
};

export const useGalleryDynamicSectionHeaders = (
  sections: GallerySectionHeader[],
): { isHeaderCollapsed: boolean; header: GallerySectionAddition<GalleryMetadata> }[] => {
  const [isHeaderCollapsed, setHeaderCollapsed] = useState<{ [key: string]: boolean }>({});
  const { isAboveMediumScreen } = useBreakpointsContext();

  const headerBoxes = useMemo(() => {
    return sections.reduce<Record<string, GallerySectionAddition<GalleryMetadata>['box']>>((acc, s) => {
      const baseHeight = s.isFirstSection
        ? !isAboveMediumScreen
          ? WKSPC_GALLERY_FIRST_SECTION_GAP_MOBILE
          : WKSPC_GALLERY_FIRST_SECTION_GAP_DESKTOP
        : SECTIONS_DISTANCE + 16;

      const spacing = !s.isFirstSection ? 24 : 0;

      acc[s.groupName] = {
        aspectRatio: 0,
        left: s.containerPadding + 5,
        top: 0,
        width: s.containerWidth - s.containerPadding,
        height: baseHeight + spacing,
      };
      return acc;
    }, {});
  }, [sections, isAboveMediumScreen]);

  const renderHeader = useCallback(
    (section: GallerySectionHeader) => {
      return (
        <ListSectionHeader
          headerTitle={section.title}
          className={!section.isFirstSection ? 'pt-6' : ''}
          collapsed={isHeaderCollapsed[section.groupName] ?? false}
          showDowntick="always"
          onClick={() => {
            setHeaderCollapsed((collapsed) => {
              section.onSectionCollapse?.(!collapsed[section.groupName]);
              return { ...collapsed, [section.groupName]: !collapsed[section.groupName] };
            });
          }}
        />
      );
    },
    [isHeaderCollapsed],
  );

  return sections.map((section) => ({
    isHeaderCollapsed: isHeaderCollapsed[section.groupName] ?? false,
    header: {
      box: headerBoxes[section.groupName],
      render: () => renderHeader(section),
    },
  }));
};
